<template>
  <EditorTabFrame
    :loading="loading"
    :loaded="loaded"
    :load-error="loadError"
    :saving="saving"
    :dirty="isDirty"
    :conflict-message="conflictMessage"
    @save="save"
    @discard="discard"
    @reload="load"
  >
    <div v-if="form && lookups">
      <EditorSection title="Salesforce e simulador" description="Envio de doadores ao CRM e o percentual usado no simulador de imposto de renda.">
        <div class="ed-grid">
          <div>
            <v-select
              v-model="form.salesForceIntegrationId"
              :items="lookups.salesForceIntegrations"
              item-value="id"
              item-text="name"
              label="Integração com Salesforce"
              hint="Vazio desliga a integração"
              persistent-hint
              clearable
            ></v-select>
          </div>
          <div>
            <v-text-field
              v-model="form.simulatorPercentage"
              type="number"
              min="0"
              max="100"
              suffix="%"
              label="Dedução no imposto de renda"
              hint="Usado no simulador e na variável de percentual do FAQ"
              persistent-hint
            ></v-text-field>
          </div>
        </div>
      </EditorSection>

      <EditorSection title="APIs de funcionários" description="Consultas que validam se a pessoa é funcionária. Chaves na URL aparecem mascaradas: deixe a máscara para manter a chave atual.">
        <template slot="aside">
          <v-btn small depressed color="primary" class="ed-pill-btn" @click="addApi"><i class="fal fa-plus mr-2"></i>Adicionar API</v-btn>
        </template>
        <div v-if="!form.apis.length" class="ed-empty">Nenhuma API cadastrada.</div>
        <div v-for="(api, index) in form.apis" :key="index" class="ed-panel">
          <div class="ed-grid">
            <div><v-text-field v-model="api.apiName" label="Nome"></v-text-field></div>
            <div><v-text-field v-model="api.url" label="URL"></v-text-field></div>
            <div v-for="field in apiFields" :key="field.key">
              <v-text-field v-model="api[field.key]" :label="field.label"></v-text-field>
            </div>
          </div>
          <v-btn small flat color="error" class="ed-text-btn px-0" @click="removeApi(index)"><i class="fal fa-trash mr-2"></i>Remover API</v-btn>
        </div>
      </EditorSection>

      <EditorSection title="Login por SSO" description="Entrada com a conta corporativa (Microsoft Entra ID / Azure AD).">
        <EditorSwitch v-if="!ssoExists" v-model="form.sso.enabled" label="Configurar login por SSO nesta campanha" />
        <div v-if="ssoExists || form.sso.enabled">
          <div class="ed-grid">
            <div><v-text-field v-model="form.sso.tenantId" label="Tenant ID"></v-text-field></div>
            <div><v-text-field v-model="form.sso.clientId" label="Client ID"></v-text-field></div>
            <div>
              <v-text-field
                v-model="form.sso.clientSecret"
                label="Client secret"
                :hint="ssoExists ? 'Deixe como está para manter o segredo atual' : ''"
                persistent-hint
              ></v-text-field>
            </div>
            <div><v-text-field v-model="form.sso.urlMetaData" label="URL de metadados"></v-text-field></div>
            <div><v-text-field v-model="form.sso.buttonText" label="Texto do botão de login"></v-text-field></div>
          </div>
          <div class="ed-grid mt-2">
            <EditorImageField label="Imagem do botão" :current-path="ssoLogoPath" v-model="newSsoLogo" />
            <div>
              <EditorSwitch v-model="form.sso.overrideDocument" label="Substituir o documento do usuário pelo do SSO" />
              <EditorSwitch v-model="form.sso.addEmployee" label="Cadastrar o usuário como funcionário" />
              <EditorSwitch v-model="form.sso.isItauSSO" label="SSO do Itaú" />
            </div>
          </div>
          <p class="ed-help mt-3 mb-0">
            Para desativar o SSO, desligue "Permitir apenas login por SSO" na seção Geral. Excluir a configuração ainda não é possível por aqui.
          </p>
        </div>
      </EditorSection>
    </div>
  </EditorTabFrame>
</template>

<script>
import editorTab from "../editorTab.mixin";
import EditorTabFrame from "../EditorTabFrame.vue";
import EditorSection from "../EditorSection.vue";
import EditorSwitch from "../EditorSwitch.vue";
import EditorImageField from "../EditorImageField.vue";
import CampaignEditorService from "@/scripts/services/campaignEditor.service";

const service = new CampaignEditorService();

export default {
  name: "IntegrationsTab",
  mixins: [editorTab],
  components: { EditorSection, EditorTabFrame, EditorSwitch, EditorImageField },
  data() {
    return {
      lookups: null,
      ssoExists: false,
      ssoLogoPath: null,
      newSsoLogo: null,
      apiFields: Object.freeze([
        { key: "cpf", label: "Campo de CPF na API" },
        { key: "name", label: "Campo de nome" },
        { key: "email", label: "Campo de e-mail" },
        { key: "registration", label: "Campo de matrícula" },
        { key: "departament", label: "Campo de departamento" },
        { key: "cnpj", label: "Campo de CNPJ" },
      ]),
    };
  },
  methods: {
    fetchSection() {
      return Promise.all([service.getLookups(), service.getIntegrations(this.campaignId)]).then(([lookups, dto]) => {
        this.lookups = lookups;
        return dto;
      });
    },
    saveSection(body) {
      return service.saveIntegrations(body);
    },
    toForm(dto) {
      const sso = dto.sso || {};
      return {
        salesForceIntegrationId: dto.salesForceIntegrationId,
        simulatorPercentage: dto.simulatorPercentage === null || dto.simulatorPercentage === undefined ? "" : dto.simulatorPercentage,
        apis: (dto.apis || []).map((api) => Object.assign({}, api)),
        sso: {
          enabled: !!sso.enabled,
          tenantId: sso.tenantId || "",
          clientId: sso.clientId || "",
          clientSecret: sso.clientSecret || "",
          urlMetaData: sso.urlMetaData || "",
          buttonText: sso.buttonText || "",
          addEmployee: !!sso.addEmployee,
          overrideDocument: !!sso.overrideDocument,
          isItauSSO: !!sso.isItauSSO,
        },
      };
    },
    afterLoad(dto) {
      this.ssoExists = !!(dto.sso && dto.sso.enabled);
      this.ssoLogoPath = dto.sso && dto.sso.logo ? dto.sso.logo.path : null;
      this.newSsoLogo = null;
    },
    afterSave() {
      if (this.newSsoLogo) this.ssoLogoPath = this.newSsoLogo.dataUri;
      this.newSsoLogo = null;
      if (this.form.sso.enabled) this.ssoExists = true;
    },
    hasPendingFiles() {
      return !!this.newSsoLogo;
    },
    addApi() {
      this.form.apis.push({ id: 0, apiName: "", url: "", cpf: "", name: "", email: "", registration: "", departament: "", cnpj: "" });
    },
    removeApi(index) {
      if (window.confirm("Remover esta API da campanha?")) this.form.apis.splice(index, 1);
    },
    validateForm() {
      const percentage = this.form.simulatorPercentage;
      if (percentage !== "" && percentage !== null && (Number(percentage) < 0 || Number(percentage) > 100)) {
        this.$store.commit("SET_SNACKBAR_MESSAGE", "A porcentagem de dedução deve estar entre 0 e 100");
        return false;
      }
      return true;
    },
    buildPayload() {
      const percentage = this.form.simulatorPercentage;
      return {
        salesForceIntegrationId: this.form.salesForceIntegrationId || null,
        simulatorPercentage: percentage === "" || percentage === null ? null : parseInt(percentage, 10),
        apis: this.form.apis,
        sso: Object.assign({}, this.form.sso, { enabled: this.ssoExists || this.form.sso.enabled, newLogo: this.newSsoLogo }),
      };
    },
  },
};
</script>

<style scoped>
.api-card {
  border: 1px solid #e6e8ef;
  border-radius: 8px;
}
</style>
